import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Typography,
  Snackbar,
} from '@mui/material';

import {
  getAllUsers,
  addUserToClub,
  checkIfUserIsAdminForClub,
  addUserAsAdmin,
} from '../Helper';

const Members = ({ members = [], clubId, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [addingUserIds, setAddingUserIds] = useState([]);
  const [localMembers, setLocalMembers] = useState(members);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [adminStatus, setAdminStatus] = useState({});
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);

  /* ---------------------------------------------
     Load all users
  --------------------------------------------- */
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const allUsers = await getAllUsers();
        setUsers(allUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  /* ---------------------------------------------
     Sync members from props
  --------------------------------------------- */
  useEffect(() => {
    setLocalMembers(members);
  }, [members]);

  /* ---------------------------------------------
     Resolve current user's school
  --------------------------------------------- */
  const currentUserRecord = users.find(u => u.uid === currentUser?.uid);
  const currentUserSchool = currentUserRecord?.schoolName;

  /* ---------------------------------------------
     Only members from same school
  --------------------------------------------- */
  const sameSchoolMembers = localMembers.filter(
    m => m.schoolName === currentUserSchool
  );

  /* ---------------------------------------------
     Check if current user is admin
  --------------------------------------------- */
  useEffect(() => {
    const fetchAdminStatus = async () => {
      try {
        const isAdmin = await checkIfUserIsAdminForClub(clubId, currentUser.uid);
        setIsCurrentUserAdmin(isAdmin);
      } catch (error) {
        console.error("Error checking admin status:", error);
        setIsCurrentUserAdmin(false);
      }
    };

    if (clubId && currentUser?.uid) {
      fetchAdminStatus();
    }
  }, [clubId, currentUser?.uid]);

  /* ---------------------------------------------
     Fetch admin status for same-school members only
  --------------------------------------------- */
  useEffect(() => {
    const fetchAdminStatuses = async () => {
      const statusObj = {};
      await Promise.all(
        sameSchoolMembers.map(async (member) => {
          try {
            const isAdmin = await checkIfUserIsAdminForClub(clubId, member.uid);
            statusObj[member.uid] = isAdmin;
          } catch {
            statusObj[member.uid] = false;
          }
        })
      );
      setAdminStatus(statusObj);
    };

    if (sameSchoolMembers.length > 0) {
      fetchAdminStatuses();
    } else {
      setAdminStatus({});
    }
  }, [sameSchoolMembers, clubId]);

  /* ---------------------------------------------
     Add User
  --------------------------------------------- */
  const handleAddUser = async (userId) => {
    const user = users.find((u) => u.uid === userId);
    if (!user) return;

    setAddingUserIds((prev) => [...prev, userId]);

    try {
      await addUserToClub(clubId, userId, 'user');
      setLocalMembers((prev) => [...prev, user]);
      setSnackbarOpen(true);
    } catch (error) {
      console.error(`Error adding user ${userId}:`, error);
    } finally {
      setAddingUserIds((prev) => prev.filter((id) => id !== userId));
    }
  };

  /* ---------------------------------------------
     Make Admin
  --------------------------------------------- */
  const handleMakeAdmin = async (userId) => {
    try {
      await addUserAsAdmin(clubId, userId);
      setAdminStatus((prev) => ({
        ...prev,
        [userId]: true,
      }));
    } catch (error) {
      console.error(`Error promoting user ${userId} to admin:`, error);
    }
  };

  /* ---------------------------------------------
     Filter add-user list to same school
  --------------------------------------------- */
  const memberIds = localMembers.map((m) => m.uid);

  const filteredUsers = users
    .filter(
      (user) =>
        user.schoolName === currentUserSchool &&
        (user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchQuery.toLowerCase())) &&
        !memberIds.includes(user.uid)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  /* ---------------------------------------------
     Render
  --------------------------------------------- */
  return (
    <Box p={2}>
      {isCurrentUserAdmin && (
        <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            ➕ Add Users to Club (Same School Only)
          </Typography>

          <TextField
            fullWidth
            label="Search by name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
          />

          {loadingUsers ? (
            <CircularProgress />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <TableRow key={user.uid}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell align="right">
                          <Button
                            variant="contained"
                            size="small"
                            disabled={addingUserIds.includes(user.uid)}
                            onClick={() => handleAddUser(user.uid)}
                          >
                            {addingUserIds.includes(user.uid) ? 'Adding...' : 'Add'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No users from your school found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          👥 Club Members (Your School)
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sameSchoolMembers.length > 0 ? (
                sameSchoolMembers.map((member) => (
                  <TableRow key={member.uid}>
                    <TableCell>
                      {member.name}{' '}
                      {adminStatus[member.uid] && (
                        <Typography component="span" color="primary" fontWeight="bold">
                          (Admin)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell align="right">
                      {!adminStatus[member.uid] && isCurrentUserAdmin && (
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={() => handleMakeAdmin(member.uid)}
                        >
                          Make Admin
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No members from your school found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="User added to club successfully"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Members;
